import type { RuleSet } from 'styled-components'
import { css, styled } from 'styled-components'
import { tk } from '../../utils/styling/get-token'

import { withStyleModifiers } from '../../utils/typescript'
import { Icon } from '../Icon'
import { Typography } from '../Typography'
import { wrapTextChildrenWithTypography } from '../Typography/wrap-text-children-with-typography'

export type LabelSize = 'default' | 'small'
export type LabelState = 'brand' | 'canceled' | 'discount' | 'error' | 'info' | 'success' | 'warning'

export type LabelVariant = 'default' | 'corner'

interface LabelProps {
  $size?: LabelSize
  $state?: LabelState
  $variant?: LabelVariant
}

const Label = styled.div.attrs<LabelProps>(({ children }) => ({
  children: wrapTextChildrenWithTypography(children),
}))`
  align-items: center;
  display: inline-flex;
  justify-content: center;

  & > * ~ * {
    margin-left: ${tk('spacing_2xs')};
  }

  ${({ $state }) => modState($state)}
  ${({ $size }) => modSize($size)}
  ${({ $variant }) => modVariant($variant)};
`

Label.displayName = 'Label'

function modVariant(variantName: LabelVariant = 'default'): RuleSet<object> {
  return ({
    corner: css`
      border-radius: 0 0 ${tk('radius_large')};
    `,
    default: css`
      border-radius: ${tk('radius_medium')};
    `,
  })[variantName]
}

function modState(stateName: LabelState = 'brand'): RuleSet<object> {
  return ({
    brand: css`
      background-color: ${tk('color_semantic_bg_primary')};
      color: ${tk('color_semantic_text_on_color')};
    `,
    canceled: css`
      background-color: ${tk('color_semantic_bg_inverse')};
      color: ${tk('color_semantic_text_on_color')};
    `,
    discount: css`
      background-color: ${tk('color_semantic_bg_error_subdued')};
      color: ${tk('color_semantic_text_error')};
    `,
    error: css`
      background-color: ${tk('color_semantic_bg_error')};
      color: ${tk('color_semantic_text_on_color')};
    `,
    success: css`
      background-color: ${tk('color_semantic_bg_success')};
      color: ${tk('color_semantic_text_on_color')};
    `,
    warning: css`
      background-color: ${tk('color_semantic_bg_warning')};
      color: ${tk('color_semantic_text_on_color')};
    `,
    info: css`
      background-color: ${tk('color_semantic_bg_strong')};
      color: ${tk('color_semantic_text_strong')};
    `,
  })[stateName]
}

function modSize(sizeName: LabelSize = 'default'): RuleSet<object> {
  return ({
    small: css`
      padding: 2px ${tk('spacing_2xs')};

      ${Icon} {
        display: none;
      }

      ${Typography} {
        ${Typography.modVariant('body-xs-bold')}
      }
    `,
    default: css`
      padding: ${tk('spacing_2xs')} ${tk('spacing_xs')};

      ${Icon} {
        ${Icon.modSize('sm')}
      }

      ${Typography} {
        ${Typography.modVariant('body-sm-bold')}
      }
    `,
  })[sizeName]
}

/**
 * @deprecated Discuss with Design Team if another component in `@sunwebgroup/ui-components/uds` is needed instead.
 */
export default withStyleModifiers(Label, {
  modSize,
})

export type { LabelProps }
